import { createSlice, createEntityAdapter, nanoid, PayloadAction } from '@reduxjs/toolkit';

export type ToastLevel = 'success' | 'error' | 'info' | 'warning';

export interface ToastAction {
  label: string;
  href?: string;
  onClick?: () => void;
}

export interface Toast {
  id: string;
  message: string;
  subtitle?: string; // Optional subtitle for ToastBody
  level: ToastLevel; //Indicates whether to display a success, error, info, or warning fluent message bar
  scope: string; //Indicates the page / location of the scope
  ttlMs?: number;
  createdAt: number;
  actions?: ToastAction[]; // Footer actions
  close?: () => void; // Optional close callback
  emphasizedText?: string;
  //Add more properties as needed e.g. action callbacks to include, etc.
}

interface AddToastPayload {
  id?: string;
  message: string;
  subtitle?: string;
  level?: ToastLevel;
  ttlMs?: number;
  scope?: string;
  actions?: ToastAction[];
  close?: () => void;
  emphasizedText?: string;
}

interface ToastExtraState {
  defaultTtlMs: number;
}

// Simple state interface for selectors
interface SimpleToastState {
  toasts: {
    ids: string[];
    entities: Record<string, Toast>;
    defaultTtlMs: number;
  };
}

const toastsAdapter = createEntityAdapter<Toast>({
  // Oldest first (top to bottom). If you want newest first in the UI, either
  // reverse when rendering or invert this comparer.
  sortComparer: (a, b) => a.createdAt - b.createdAt,
});

const initialState = toastsAdapter.getInitialState<ToastExtraState>({
  defaultTtlMs: 4000,
});

const toastSlice = createSlice({
  name: 'toasts',
  initialState,
  reducers: {
    addToast: {
      prepare: (payload: AddToastPayload) => {
        const id = payload.id ?? nanoid();
        return {
          payload: {
            id,
            message: payload.message,
            subtitle: payload.subtitle,
            level: payload.level ?? 'info',
            scope: payload.scope ?? 'global',
            ttlMs: payload.ttlMs,
            createdAt: Date.now(),
            actions: payload.actions,
            close: payload.close,
            emphasizedText: payload.emphasizedText,
          } as Toast,
        };
      },
      reducer: (state, action: PayloadAction<Toast>) => {
        toastsAdapter.addOne(state, action.payload);
      },
    },
    removeToast: (state, action: PayloadAction<string>) => {
      toastsAdapter.removeOne(state, action.payload);
    },
    clearToasts: (state) => {
      toastsAdapter.removeAll(state);
    },
    // Optional: update default ttl at runtime
    setDefaultTtl: (state, action: PayloadAction<number>) => {
      state.defaultTtlMs = action.payload;
    },
  },
});

export const { addToast, removeToast, clearToasts, setDefaultTtl } = toastSlice.actions;

export default toastSlice.reducer;

// Selectors - Using a simple approach to avoid complex type dependencies
export const selectAllToasts = (state: SimpleToastState) => {
  const toastState = state.toasts;
  if (!toastState) return [];
  return Object.values(toastState.entities || {}) as Toast[];
};

export const selectToastById = (state: SimpleToastState, id: string) => {
  const toastState = state.toasts;
  if (!toastState || !toastState.entities) return undefined;
  return toastState.entities[id] as Toast | undefined;
};

export const selectToastIds = (state: SimpleToastState) => {
  const toastState = state.toasts;
  if (!toastState) return [];
  return toastState.ids || [];
};

export const selectToastEntities = (state: SimpleToastState) => {
  const toastState = state.toasts;
  if (!toastState) return {};
  return toastState.entities || {};
};

export const selectTotalToasts = (state: SimpleToastState) => {
  const toastState = state.toasts;
  if (!toastState) return 0;
  return (toastState.ids || []).length;
};

// Additional selectors
export const selectDefaultTtl = (state: SimpleToastState) => {
  const toastState = state.toasts;
  return toastState?.defaultTtlMs || 4000;
};

export const selectToastsByScope = (scope: string) => (state: SimpleToastState) => {
  const allToasts = selectAllToasts(state);
  return allToasts.filter((toast) => toast.scope === scope);
};

export const selectToastsByLevel = (level: ToastLevel) => (state: SimpleToastState) => {
  const allToasts = selectAllToasts(state);
  return allToasts.filter((toast) => toast.level === level);
};
// Generated by GitHub Copilot | Updated on: 2026-10-09 | 4 lines modified | Stored emphasized toast text
